
import { useState } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslations } from "@/hooks/useTranslations";

const defaultFormSchema = z.object({
  defaultSets: z.coerce.number().min(1, "Mínimo 1 série"),
  defaultReps: z.string().min(1, "Necessário preencher repetições"),
  defaultTime: z.string().optional(),
});

type DefaultFormValues = z.infer<typeof defaultFormSchema>;

interface MultiSelectFormProps {
  selectedCount: number;
  onAddExercises: (defaultSets: number, defaultReps: string, defaultTime?: string) => void;
}

export function MultiSelectForm({ selectedCount, onAddExercises }: MultiSelectFormProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslations("workouts");
  
  const form = useForm<DefaultFormValues>({
    resolver: zodResolver(defaultFormSchema),
    defaultValues: {
      defaultSets: 3,
      defaultReps: "8-12",
      defaultTime: "",
    },
  });

  const handleSubmit = (data: DefaultFormValues) => {
    onAddExercises(
      data.defaultSets,
      data.defaultReps,
      data.defaultTime
    );
  };

  return (
    <Card className="bg-muted/20 mb-2 dark:bg-gray-800/50 dark:border-gray-700/50">
      <CardContent className="pt-4 px-4">
        <Form {...form}>
          <div className="grid grid-cols-3 gap-2">
            <FormField
              control={form.control}
              name="defaultSets"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-white">{t("multiSelect.defaultSets")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      className="bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="defaultReps"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-white">{t("multiSelect.defaultReps")}</FormLabel>
                  <FormControl>
                    <Input
                      className="bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder={t("multiSelect.repsPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="defaultTime"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-white">{t("multiSelect.optionalTime")}</FormLabel>
                  <FormControl>
                    <Input
                      className="bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder={t("multiSelect.timePlaceholder")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-end pt-1 pb-3 px-4">
        <Button onClick={() => form.handleSubmit(handleSubmit)()} className="whitespace-nowrap">
          <Plus className="mr-2 h-4 w-4" />
          {t("multiSelect.addExercises").replace("{{count}}", selectedCount.toString())}
        </Button>
      </CardFooter>
    </Card>
  );
}
